import React, { useEffect, useRef, useState } from 'react';
import { loadYouTubeAPI, extractYouTubeVideoId } from '../../../utils/youtubeUtils';

/**
 * YouTube Player Component with API integration
 * Detects when video ends and calls onVideoEnd callback
 */
function YouTubePlayer({ video, autoPlay = false, onVideoEnd, onError }) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const isMountedRef = useRef(true);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [error, setError] = useState(null);
  const videoId = extractYouTubeVideoId(video.url);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load YouTube API on mount
  useEffect(() => {
    loadYouTubeAPI()
      .then(() => {
        if (isMountedRef.current) {
          setApiLoaded(true);
        }
      })
      .catch((err) => {
        if (isMountedRef.current) {
          setError(err.message);
          onError?.(err);
        }
      });
  }, [onError]);

  // Create player instance when API is loaded
  useEffect(() => {
    if (!apiLoaded || !containerRef.current || !videoId) return;

    // Create unique ID for container
    const containerId = `youtube-player-${Date.now()}`;
    containerRef.current.id = containerId;

    try {
      const player = new window.YT.Player(containerId, {
        videoId,
        playerVars: {
          autoplay: autoPlay ? 1 : 0,
          enablejsapi: 1,
          rel: 0,
          modestbranding: 1,
          fs: 1,
          iv_load_policy: 3,
        },
        events: {
          onStateChange: (event) => {
            // Only call callback if component is still mounted
            if (!isMountedRef.current) return;

            // Detect when video ends (state code 0)
            if (event.data === window.YT.PlayerState.ENDED) {
              onVideoEnd?.();
            }
          },
          onError: (event) => {
            // Only handle error if component is still mounted
            if (!isMountedRef.current) return;

            // Map error codes to user-friendly messages
            const errorMessages = {
              2: 'Invalid video ID',
              5: 'Video cannot be played in HTML5 player',
              100: 'Video not found or has been removed',
              101: 'Video owner has disabled embedding',
              150: 'Video owner has disabled embedding',
              153: 'Video owner has disabled embedding',
            };

            const errorMsg = errorMessages[event.data] || `YouTube player error: ${event.data}`;
            setError(errorMsg);
            onError?.(new Error(errorMsg));
          },
        },
      });

      playerRef.current = player;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.message);
        onError?.(err);
      }
    }

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (err) {
          // Silently handle destroy errors
        }
      }
      playerRef.current = null;
    };
  }, [apiLoaded, videoId, autoPlay, onVideoEnd, onError]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center p-4">
          <p className="text-red-400 mb-2">Failed to load YouTube player</p>
          <p className="text-sm text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!videoId) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white">
        <p className="text-red-400">Invalid YouTube video URL</p>
      </div>
    );
  }

  return <div ref={containerRef} className="w-full h-full" />;
}

export default YouTubePlayer;
